import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import UndoIcon from "@mui/icons-material/Undo";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  InputAdornment,
  Link,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import FulfillmentWorkQueueApi from "../../../../../infrastructure/api/fulfillmentWorkQueue";
import {
  getApiOrigin,
  resolveUploadUrl,
} from "../../../../../infrastructure/utils/resolveUploadUrl";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import FileUploader from "../../../../components/shared/uploader/FileUploader";
import { toJalaliFullDate } from "../../../../middleware/middleware";
import FulfillmentDispatchDialog from "./FulfillmentDispatchDialog";

const PAGE_TITLE = "کارتابل تامین، ارسال و اجرا";
const OPERATIONS_SOURCE = "SolarentOperator";

export const QUEUE_TABS = [
  { key: "awaitingSupply", label: "در انتظار تامین تجهیزات", countField: "awaitingSupplyCount" },
  { key: "awaitingShipment", label: "در انتظار ارسال تجهیزات", countField: "awaitingShipmentCount" },
  { key: "inExecution", label: "در حال اجرا", countField: "inExecutionCount" },
  { key: "completed", label: "اجرا تکمیل شده", countField: "completedCount" },
];

const ACTIONS = {
  supply: {
    title: "ثبت تامین تجهیزات",
    dateLabel: "تاریخ تامین",
    call: FulfillmentWorkQueueApi.registerSupply,
    nextTab: "awaitingShipment",
  },
  shipment: {
    title: "ثبت ارسال تجهیزات",
    dateLabel: "تاریخ ارسال",
    call: FulfillmentWorkQueueApi.registerShipment,
    nextTab: "inExecution",
  },
  execution: {
    title: "ثبت اتمام اجرا",
    dateLabel: "تاریخ اتمام اجرا",
    call: FulfillmentWorkQueueApi.completeExecution,
    nextTab: "completed",
  },
};

const REVERTED_ACTION_LABELS = {
  awaitingShipment: "تامین تجهیزات",
  inExecution: "ارسال تجهیزات",
  completed: "اتمام اجرا",
};

const EMPTY_COUNTS = {
  awaitingSupplyCount: 0,
  awaitingShipmentCount: 0,
  inExecutionCount: 0,
  completedCount: 0,
};

const EMPTY_FORM = { actionDate: null, note: "", trackingNumber: "", fileUrl: "" };

const pick = (raw, camel) =>
  raw?.[camel] ?? raw?.[camel.charAt(0).toUpperCase() + camel.slice(1)] ?? null;

const normalizeRow = (raw) => ({
  requestInfoId: pick(raw, "requestInfoId"),
  requestTrackingCode: pick(raw, "requestTrackingCode"),
  applicantFullName: pick(raw, "applicantFullName") || "",
  applicantMobile: pick(raw, "applicantMobile") || "",
  plantCapacityKw: pick(raw, "plantCapacityKw"),
  provinceTitle: pick(raw, "provinceTitle") || "",
  cityTitle: pick(raw, "cityTitle") || "",
  contractNumber: pick(raw, "contractNumber") || "",
  firstInstallmentAmount: Number(pick(raw, "firstInstallmentAmount")) || 0,
  approvedPaidAmount: Number(pick(raw, "approvedPaidAmount")) || 0,
  pendingApprovalAmount: Number(pick(raw, "pendingApprovalAmount")) || 0,
  isAwaitingPaymentApproval: Boolean(pick(raw, "isAwaitingPaymentApproval")),
  firstInstallmentPaidAt: pick(raw, "firstInstallmentPaidAt"),
  queueTab: pick(raw, "queueTab"),
  stageTitle: pick(raw, "stageTitle") || "",
  suppliedAt: pick(raw, "suppliedAt"),
  supplyNote: pick(raw, "supplyNote") || "",
  shippedAt: pick(raw, "shippedAt"),
  shipmentTrackingNumber: pick(raw, "shipmentTrackingNumber") || "",
  shipmentNote: pick(raw, "shipmentNote") || "",
  shipmentFileUrl: pick(raw, "shipmentFileUrl") || "",
  executionCompletedAt: pick(raw, "executionCompletedAt"),
  executionNote: pick(raw, "executionNote") || "",
  openDays: Number(pick(raw, "openDays")) || 0,
});

const formatRial = (value) =>
  Number(value) > 0 ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const formatDate = (value) => (value ? toJalaliFullDate(value) || "—" : "—");

const FulfillmentWorkQueuePage = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(EMPTY_COUNTS);
  const [activeTab, setActiveTab] = useState("awaitingSupply");
  const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [actionDialog, setActionDialog] = useState({ type: null, row: null });
  const [dispatchRequestId, setDispatchRequestId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [revertRow, setRevertRow] = useState(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = appliedSearch.trim() ? { searchText: appliedSearch.trim() } : {};
      const res = await FulfillmentWorkQueueApi.search(OPERATIONS_SOURCE, payload);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در دریافت کارتابل");
        setRows([]);
        setCounts(EMPTY_COUNTS);
        return;
      }
      const data = res?.data ?? res;
      const list = data?.rows ?? data?.Rows ?? [];
      setRows(Array.isArray(list) ? list.map(normalizeRow) : []);
      setCounts({
        awaitingSupplyCount: pick(data, "awaitingSupplyCount") || 0,
        awaitingShipmentCount: pick(data, "awaitingShipmentCount") || 0,
        inExecutionCount: pick(data, "inExecutionCount") || 0,
        completedCount: pick(data, "completedCount") || 0,
      });
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت کارتابل");
      setRows([]);
      setCounts(EMPTY_COUNTS);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openAction = useCallback((type, row) => {
    setForm(EMPTY_FORM);
    setActionDialog({ type, row });
  }, []);

  const closeAction = () => {
    if (!saving) setActionDialog({ type: null, row: null });
  };

  const handleActionConfirm = async () => {
    const { type, row } = actionDialog;
    const action = ACTIONS[type];
    if (!action || !row?.requestInfoId) return;

    setSaving(true);
    try {
      const res = await action.call(OPERATIONS_SOURCE, {
        requestId: row.requestInfoId,
        actionDate: form.actionDate || null,
        note: form.note || null,
        trackingNumber: type === "shipment" ? form.trackingNumber || null : null,
        fileUrl: type === "shipment" ? form.fileUrl || null : null,
      });
      if (res?.isSuccess === false) {
        toast.error(res?.message || "ثبت اقدام ناموفق بود.");
        return;
      }
      toast.success(res?.message || "ثبت شد");
      setActionDialog({ type: null, row: null });
      setActiveTab(action.nextTab);
      await loadList();
    } catch (e) {
      toast.error(e?.message || "ثبت اقدام ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const handleRevertConfirm = async () => {
    if (!revertRow?.requestInfoId) return;
    setSaving(true);
    try {
      const res = await FulfillmentWorkQueueApi.revertStage(
        OPERATIONS_SOURCE,
        revertRow.requestInfoId
      );
      if (res?.isSuccess === false) {
        toast.error(res?.message || "برگشت مرحله ناموفق بود.");
        return;
      }
      toast.success(res?.message || "پروژه به مرحله قبل برگشت");
      setRevertRow(null);
      await loadList();
    } catch (e) {
      toast.error(e?.message || "برگشت مرحله ناموفق بود.");
    } finally {
      setSaving(false);
    }
  };

  const tableRows = useMemo(
    () =>
      rows
        .filter((r) => r.queueTab === activeTab)
        .map((row, index) => ({
          ...row,
          row_index: index + 1,
          capacityDisplay: row.plantCapacityKw ? `${row.plantCapacityKw} kW` : "—",
          locationDisplay: [row.provinceTitle, row.cityTitle].filter(Boolean).join(" / ") || "—",
          paidAtDisplay: formatDate(row.firstInstallmentPaidAt),
          suppliedAtDisplay: formatDate(row.suppliedAt),
          shippedAtDisplay: formatDate(row.shippedAt),
          executionCompletedAtDisplay: formatDate(row.executionCompletedAt),
          trackingDisplay: row.shipmentTrackingNumber || "—",
          openDaysDisplay: `${row.openDays} روز`,
        })),
    [rows, activeTab]
  );

  const headCells = useMemo(() => {
    const cells = [
      { id: "row_index", label: "ردیف" },
      { id: "requestTrackingCode", label: "کد پیگیری" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "applicantMobile", label: "موبایل" },
      { id: "capacityDisplay", label: "ظرفیت" },
      { id: "locationDisplay", label: "استان / شهر" },
      { id: "contractNumber", label: "شماره قرارداد" },
      {
        id: "firstInstallmentAmount",
        label: "قسط اول آورده",
        render: (value) => formatRial(value),
      },
      {
        id: "approvedPaidAmount",
        label: "پرداخت تاییدشده",
        render: (value, row) =>
          row.isAwaitingPaymentApproval ? (
            <Stack spacing={0.5} alignItems="flex-start">
              <span>{formatRial(value)}</span>
              <Chip
                size="small"
                color="warning"
                label={`در انتظار تایید مالی: ${formatRial(row.pendingApprovalAmount)}`}
              />
            </Stack>
          ) : (
            formatRial(value)
          ),
      },
      { id: "paidAtDisplay", label: "تاریخ پرداخت" },
    ];

    if (activeTab !== "awaitingSupply") cells.push({ id: "suppliedAtDisplay", label: "تاریخ تامین" });
    if (activeTab === "inExecution" || activeTab === "completed") {
      cells.push(
        { id: "shippedAtDisplay", label: "تاریخ ارسال" },
        { id: "trackingDisplay", label: "شماره بارنامه" }
      );
    }
    if (activeTab === "completed") {
      cells.push({ id: "executionCompletedAtDisplay", label: "تاریخ اتمام اجرا" });
    } else {
      cells.push({ id: "openDaysDisplay", label: "روزهای انتظار" });
    }
    return cells;
  }, [activeTab]);

  const tableActions = useMemo(
    () => [
      {
        icon: <Inventory2OutlinedIcon fontSize="small" />,
        onClick: (row) => openAction("supply", row),
        color: "primary",
        tooltip: "ثبت تامین تجهیزات",
        hidden: (row) => row.queueTab !== "awaitingSupply" || row.isAwaitingPaymentApproval,
      },
      {
        icon: <LocalShippingOutlinedIcon fontSize="small" />,
        onClick: (row) => openAction("shipment", row),
        color: "primary",
        tooltip: "ثبت ارسال تجهیزات",
        hidden: (row) => row.queueTab !== "awaitingShipment",
      },
      {
        icon: <CheckCircleOutlineIcon fontSize="small" />,
        onClick: (row) => openAction("execution", row),
        color: "success",
        tooltip: "ثبت اتمام اجرا",
        hidden: (row) => row.queueTab !== "inExecution",
      },
      {
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        onClick: (row) => setDispatchRequestId(row.requestInfoId),
        color: "primary",
        tooltip: "اطلاعات ارسال و دانلود برگه‌ها",
      },
      {
        icon: <UndoIcon fontSize="small" />,
        onClick: (row) => setRevertRow(row),
        color: "warning",
        tooltip: "برگشت به مرحله قبل",
        hidden: (row) => row.queueTab === "awaitingSupply",
      },
    ],
    [openAction]
  );

  const activeAction = ACTIONS[actionDialog.type];
  const actionRow = actionDialog.row;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>
      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={PAGE_TITLE}
            subheader="پروژه‌هایی که قرارداد امضا کرده‌اند و پرداخت متقاضی به مبلغ قسط اول آورده (پیش از شروع اجرا) رسیده است؛ ثبت تامین پس از تایید مالی پرداخت ممکن است"
            action={
              <Button
                startIcon={<SyncIcon />}
                onClick={loadList}
                disabled={loading}
                variant="outlined"
                size="small"
              >
                بروزرسانی
              </Button>
            }
          />
          <CardContent>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
              <TextField
                size="small"
                placeholder="جستجو نام / موبایل / کد پیگیری / شماره قرارداد / شهر"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && setAppliedSearch(searchText)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 320 }}
              />
              <Button variant="contained" onClick={() => setAppliedSearch(searchText)}>
                اعمال فیلتر
              </Button>
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {QUEUE_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={
                    <Badge
                      badgeContent={counts[tab.countField] || 0}
                      color={tab.key === "completed" ? "success" : "primary"}
                      sx={{ "& .MuiBadge-badge": { right: -12 } }}
                    >
                      <Typography component="span" sx={{ pr: 1.5 }}>
                        {tab.label}
                      </Typography>
                    </Badge>
                  }
                />
              ))}
            </Tabs>

            <EnhancedTable
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              actionsMode="menu"
              loading={loading}
              showCheckbox={false}
              dense
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog open={Boolean(activeAction)} onClose={closeAction} fullWidth maxWidth="sm">
        <DialogTitle>{activeAction?.title}</DialogTitle>
        <DialogContent>
          {actionRow ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                کد پیگیری: {actionRow.requestTrackingCode}
                {actionRow.applicantFullName ? ` — ${actionRow.applicantFullName}` : ""}
              </Typography>
              <Typography variant="body2">
                ظرفیت: {actionRow.plantCapacityKw ? `${actionRow.plantCapacityKw} kW` : "—"}
                {" — "}
                {[actionRow.provinceTitle, actionRow.cityTitle].filter(Boolean).join(" / ") || "—"}
              </Typography>
              {actionDialog.type === "execution" && actionRow.shipmentTrackingNumber ? (
                <Typography variant="body2">
                  شماره بارنامه: {actionRow.shipmentTrackingNumber}
                </Typography>
              ) : null}
              {actionDialog.type === "execution" && actionRow.shipmentFileUrl ? (
                <Link
                  href={resolveUploadUrl(actionRow.shipmentFileUrl, getApiOrigin())}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                >
                  مشاهده فایل ارسال
                </Link>
              ) : null}
            </Box>
          ) : null}
          <Stack spacing={1.5}>
            <JalaliDatePicker
              label={activeAction?.dateLabel}
              value={form.actionDate}
              size="small"
              fullWidth
              helperText="خالی = امروز"
              onChange={(v) => setForm((f) => ({ ...f, actionDate: v }))}
            />
            {actionDialog.type === "shipment" ? (
              <>
                <TextField
                  label="شماره بارنامه / پیگیری ارسال"
                  size="small"
                  fullWidth
                  value={form.trackingNumber}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, trackingNumber: e.target.value }))}
                />
                <FileUploader
                  moduleName="SLN"
                  label="فایل بارنامه / رسید تحویل (اختیاری)"
                  accept="image/*,application/pdf"
                  maxFiles={1}
                  baseUrl={getApiOrigin()}
                  value={form.fileUrl}
                  onChange={(url) =>
                    setForm((f) => ({
                      ...f,
                      fileUrl: Array.isArray(url) ? url[0] || "" : url || "",
                    }))
                  }
                />
              </>
            ) : null}
            <TextField
              label="توضیحات"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={form.note}
              disabled={saving}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAction} disabled={saving}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleActionConfirm}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "در حال ثبت…" : "ثبت"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(revertRow)} onClose={() => !saving && setRevertRow(null)}>
        <DialogTitle>برگشت به مرحله قبل</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ثبت «{REVERTED_ACTION_LABELS[revertRow?.queueTab] || "مرحله فعلی"}» برای پروژه
            {revertRow?.requestTrackingCode ? ` ${revertRow.requestTrackingCode}` : ""} پاک
            می‌شود و پروژه یک مرحله به عقب برمی‌گردد. ادامه می‌دهید؟
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevertRow(null)} disabled={saving}>
            انصراف
          </Button>
          <Button color="warning" variant="contained" onClick={handleRevertConfirm} disabled={saving}>
            برگشت
          </Button>
        </DialogActions>
      </Dialog>

      <FulfillmentDispatchDialog
        open={Boolean(dispatchRequestId)}
        requestId={dispatchRequestId}
        onClose={() => setDispatchRequestId(null)}
      />
    </Grid>
  );
};

export default FulfillmentWorkQueuePage;
